import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Plane } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { NAV_ITEMS, CONTACT_INFO, ROUTE_PATHS } from '@/lib/index';
import { SiSinaweibo, SiWechat } from 'react-icons/si';

// ─── Header ───────────────────────────────────────────────────────────────────
function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navBg = isHome
    ? isScrolled
      ? 'bg-secondary/95 shadow-lg'
      : 'bg-transparent'
    : 'bg-secondary/95 shadow-md';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      {/* Top bar */}
      <div className="border-b border-white/10 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 h-9 flex items-center justify-between">
          <span className="text-white/70 text-xs">坦桑尼亚航空 — 乞力马扎罗之翼</span>
          <a
            href={`tel:${CONTACT_INFO.phone}`}
            className="flex items-center gap-1.5 text-accent text-xs font-medium hover:text-accent/80 transition-colors"
          >
            <Phone size={12} />
            {CONTACT_INFO.phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-md">
              <Plane size={20} className="text-white" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-bold text-base tracking-tight">Air Tanzania</div>
              <div className="text-accent text-xs font-medium">坦桑尼亚航空</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={() => item.children && setActiveDropdown(item.path)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-accent bg-white/10'
                        : 'text-white/85 hover:text-white hover:bg-white/10'
                    }`
                  }
                >
                  {item.label}
                  {item.children && <ChevronDown size={12} />}
                </NavLink>
                {item.children && activeDropdown === item.path && (
                  <AnimatePresence>
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-1 w-44 bg-secondary/98 rounded-xl shadow-xl border border-white/10 py-1 z-10"
                    >
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>
            ))}
          </nav>

          {/* Book Now CTA + Hamburger */}
          <div className="flex items-center gap-3">
            <Link
              to={ROUTE_PATHS.BOOKING}
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-semibold hover:bg-accent/90 transition-colors shadow-md"
            >
              <Plane size={14} />
              立即订票
            </Link>
            <button
              className="xl:hidden text-white p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="菜单"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="xl:hidden bg-secondary/98 border-t border-white/10 overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-accent bg-white/10'
                        : 'text-white/85 hover:text-white hover:bg-white/10'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="pt-2 pb-1">
                <Link
                  to={ROUTE_PATHS.BOOKING}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-accent text-accent-foreground rounded-lg text-sm font-semibold"
                >
                  <Plane size={14} />
                  立即订票
                </Link>
              </div>
              <a
                href={`tel:${CONTACT_INFO.phone}`}
                className="flex items-center gap-2 px-4 py-2 text-accent text-sm"
              >
                <Phone size={14} />
                {CONTACT_INFO.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="bg-secondary text-white">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <Plane size={18} className="text-white" />
              </div>
              <div>
                <div className="font-bold text-base">Air Tanzania</div>
                <div className="text-accent text-xs">坦桑尼亚国家航空</div>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              乞力马扎罗之翼<br />
              The Wings of Kilimanjaro<br />
              连接中国与非洲大陆
            </p>
            <div className="flex gap-3">
              <a
                href={CONTACT_INFO.weibo}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent/80 transition-colors"
                title="官方微博"
              >
                <SiSinaweibo size={16} />
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent/80 transition-colors"
                title="微信公众号"
              >
                <SiWechat size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-sm mb-4 text-accent uppercase tracking-wider">快速链接</h3>
            <ul className="space-y-2.5">
              {[
                { label: '航班查询', path: ROUTE_PATHS.BOOKING },
                { label: '航线与目的地', path: ROUTE_PATHS.ROUTES },
                { label: '机队介绍', path: ROUTE_PATHS.FLEET },
                { label: '出行指南', path: ROUTE_PATHS.TRAVEL_GUIDE },
                { label: 'Twiga Miles', path: ROUTE_PATHS.TWIGA_MILES },
                { label: '货运服务', path: ROUTE_PATHS.CARGO },
              ].map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-white/60 text-sm hover:text-accent transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-sm mb-4 text-accent uppercase tracking-wider">服务信息</h3>
            <ul className="space-y-2.5">
              {[
                { label: '行李规定', path: ROUTE_PATHS.TRAVEL_GUIDE },
                { label: '签证信息', path: ROUTE_PATHS.TRAVEL_GUIDE },
                { label: '中转指南', path: ROUTE_PATHS.TRAVEL_GUIDE },
                { label: '机上餐食', path: ROUTE_PATHS.TRAVEL_GUIDE },
                { label: '关于我们', path: ROUTE_PATHS.ABOUT },
                { label: '新闻公告', path: ROUTE_PATHS.NEWS },
              ].map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.path}
                    className="text-white/60 text-sm hover:text-accent transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-sm mb-4 text-accent uppercase tracking-wider">联系我们</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <span className="text-white/40 block text-xs mb-0.5">中国代表处（广州）</span>
                <a href={`tel:${CONTACT_INFO.phone}`} className="text-white/80 hover:text-accent transition-colors font-medium">
                  📞 {CONTACT_INFO.phone}
                </a>
              </li>
              <li>
                <span className="text-white/40 block text-xs mb-0.5">办公地址</span>
                <div className="text-white/60 text-xs leading-relaxed">
                  天河区林和西路9号<br />耀中广场 A座 1323室
                </div>
                <div className="flex gap-3 mt-1">
                  <a href="https://maps.google.com/maps?q=耀中广场+ChinaShine+Plaza+广州+天河区" target="_blank" rel="noopener noreferrer" className="text-accent/70 hover:text-accent text-[11px] underline underline-offset-2">Google Maps</a>
                  <a href="https://uri.amap.com/marker?position=113.334,23.141&name=耀中广场ChinaShine+Plaza&src=airtanzania&coordinate=gaode" target="_blank" rel="noopener noreferrer" className="text-accent/70 hover:text-accent text-[11px] underline underline-offset-2">高德地图</a>
                </div>
              </li>
              <li>
                <span className="text-white/40 block text-xs mb-0.5">旅客服务</span>
                <a href={`mailto:${CONTACT_INFO.passengerEmail}`} className="text-white/60 hover:text-accent transition-colors break-all">
                  {CONTACT_INFO.passengerEmail}
                </a>
              </li>
              <li>
                <span className="text-white/40 block text-xs mb-0.5">代理人服务</span>
                <a href={`mailto:${CONTACT_INFO.agentEmail}`} className="text-white/60 hover:text-accent transition-colors break-all">
                  {CONTACT_INFO.agentEmail}
                </a>
              </li>
              <li>
                <span className="text-white/40 block text-xs mb-0.5">QQ 服务群</span>
                <span className="text-white/60">{CONTACT_INFO.qqGroup}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/40">
          <p>© 2026 Air Tanzania Company Limited — 中国代表处（广州）</p>
          <p>IOSA 认证 · IATA 成员 · AFRAA 成员</p>
          <a
            href={CONTACT_INFO.website}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-accent transition-colors"
          >
            airtanzania.co.tz
          </a>
        </div>
      </div>
    </footer>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────
interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
